import { CookieMonster } from 'root/utils/cookies/cookieMonster';
import {
    getDecodedValueFromLocalStorage,
    saveEncodedValueToLocalStorage,
} from 'root/utils/dom/localStorage';
import { GoogleTagManager } from 'root/utils/tracking/GA4/googleTagManager';
import SSBO_VALUES from 'root/global_configs/ssbo_values.json';
import { appendParamsToUrl } from 'root/utils/URLParams/URLParams';

export const TALK_TO_AN_EXPERT_FORM_NAME =
    SSBO_VALUES.TALK_TO_EXPERT.form_name_set_by_cta;

const formFieldsToPassAsParams = ['estimated_annual_gtv'];

const handleSSBORedirectClick = e => {
    const target = e.currentTarget;
    const form = target.closest('form');
    const hiddenFields = form
        ? form.querySelectorAll('[data-form="hidden-field-container"] input[type=hidden]')
        : [];
    const ssboRedirectEventData = {};
    const hiddenFieldsToTrack = {
        stage: 'stage',
        business_type: 'business_type',
        number_of_locations: 'number_of_locations',
        estimated_annual_gtv: 'gtv_value',
    };

    target.href = appendParamsToUrl(target.href, SSBO_VALUES.TALK_TO_EXPERT);

    Object.entries(hiddenFieldsToTrack).forEach(([fieldName, targetKey]) => {
        const fieldValue = findHiddenFieldValue(hiddenFields, fieldName);
        let resolvedValue = null;

        if (fieldValue) {
            resolvedValue = fieldValue;
            ssboRedirectEventData[targetKey] = fieldValue;
        } else {
            const storeField = target.getAttribute('data-wizard-form-store-field');
            const [storedFieldName, storedValue] = storeField
                ? storeField.split('|')
                : [];
            if (storeField && storedFieldName === fieldName) {
                resolvedValue = storedValue;
                ssboRedirectEventData[targetKey] = storedValue;
            }
        }

        appendSsboParamToRedirectClickHref(
            target,
            fieldName,
            resolvedValue,
            formFieldsToPassAsParams
        );
    });

    const attributeNamesToTrack = ['tracking-name', 'placement'];
    attributeNamesToTrack.forEach(attributeName => {
        const attributeValue = getFormAttributeValue(form, attributeName);
        if (attributeValue) {
            if (attributeName === 'tracking-name') {
                ssboRedirectEventData['form_name'] = attributeValue;
            } else {
                ssboRedirectEventData[attributeName] = attributeValue;
            }
        }
    });
    const productType = form?.getAttribute('data-form-name')?.split('_')[0];
    if (productType) {
        ssboRedirectEventData['product_type'] = productType;
    }

    const json = JSON.stringify(ssboRedirectEventData);
    saveEncodedValueToLocalStorage('redirectEventData', json, 'ssbo');
};

const findHiddenFieldValue = (hiddenFields, fieldName) => {
    return (
        Array.from(hiddenFields).find(field => field.name === fieldName)?.value || null
    );
};

const getFormAttributeValue = (form, attributeName) => {
    if (!form) return null;
    const attribute = form.getAttribute(`data-form-${attributeName}`);
    return attribute ? attribute : null;
};

const getSSBORedirectEventData = () => {
    const ssboLocalStorageData = getDecodedValueFromLocalStorage(
        'redirectEventData',
        'ssbo'
    );
    return ssboLocalStorageData ? JSON.parse(ssboLocalStorageData) : null;
};

const setTalkToExpertFormNameCookie = () => {
    const ssboRedirectEventData = getSSBORedirectEventData();
    if (ssboRedirectEventData?.form_name !== 'talk_expert') return;
    const key = Object.keys(SSBO_VALUES.TALK_TO_EXPERT)[0];

    window.registerCookieComplianceCallback({
        callback: () => {
            const cm = new CookieMonster({ useDomain: true });
            cm.setCookie(key, TALK_TO_AN_EXPERT_FORM_NAME);
        },
        category: 'necessary',
        isCallOptanonWrapper: true,
    });
};

const bindTalkToExpertLandingPageCTAs = () => {
    const ssboLandingPage = document.querySelector('.acf-class-ssbo-landing-page');
    if (!ssboLandingPage) return;

    const ctas = ssboLandingPage.querySelectorAll(
        'a[href^="https://account"][href*=".lightspeedhq.com"]'
    );
    const ssboParams = handleSSBOUrlParams();

    ctas.forEach(cta => {
        if (cta.dataset.formNameCookieBound === 'true') return;
        cta.href = appendParamsToUrl(cta.href, ssboParams);
        cta.addEventListener('click', setTalkToExpertFormNameCookie);
        cta.dataset.formNameCookieBound = 'true';
    });
};

const handleSSBORedirectPageSeenEvent = () => {
    const ssboLandingPage = document.querySelector('.acf-class-ssbo-landing-page');
    if (!ssboLandingPage) return;

    const ssboRedirectEventData = getSSBORedirectEventData();
    if (!ssboRedirectEventData) return;

    ssboRedirectEventData['campaign'] = TALK_TO_AN_EXPERT_FORM_NAME;
    ssboRedirectEventData['event_action'] = 'view_redirect_page';
    GoogleTagManager.sendEvent(ssboRedirectEventData);

    // do we want this event to run when a user refreshes the page or navigates back to it after moving on?
    // if not, include the below line to clear the stored data after it's been used once.
    // localStorage.removeItem('ssboRedirectEventData');
};

const appendSsboParamToRedirectClickHref = (
    target,
    fieldName,
    resolvedValue,
    formFieldsToPassAsParams
) => {
    if (!formFieldsToPassAsParams.includes(fieldName)) return;
    if (!resolvedValue || !target.href) return;

    target.href = appendParamsToUrl(target.href, {
        [fieldName]: resolvedValue,
    });
};

const handleSSBOUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};

    urlParams.forEach((value, key) => {
        params[key] = value;
    });

    return params;
};

export {
    handleSSBORedirectClick,
    handleSSBORedirectPageSeenEvent,
    bindTalkToExpertLandingPageCTAs,
    setTalkToExpertFormNameCookie,
};
